import { Stack } from '@mui/joy';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import uniq from 'lodash/fp/uniq';
import { type ReactElement, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getAssetsGroups } from 'components/market/asset/groups/GroupService.ts';
import GButton from 'components/technical/inputs/GButton/GButton.tsx';
import type { AssetOptimizerInputFields } from '../AssetOptimizer.validation.ts';
import Universe, { createInInitialPortfolioColumn } from '../../universe/Universe.tsx';
import { getAssetCategory } from '../../../../../market/asset/AssetService.tsx';
import { defaultRowSpacing } from '../../../../../StackSpacing.ts';
import { syncOutlookWithGivenUniverseFields } from '../../assumptionsAndOutlook/AssumptionsAndOutlook.utils.ts';
import type { NotVerifiedAssetWithId } from 'components/market/asset/AssetService.tsx';

const AssetUniverseStep = ({
  assets,
  assetIdToClusterToGroup,
  goToNextStep,
  syncWithOutlook = true,
  hideInitialPortfolio = false,
}: {
  assets: NotVerifiedAssetWithId[];
  goToNextStep: () => void;
  assetIdToClusterToGroup: Record<string, Record<string, string>>;
  syncWithOutlook: boolean;
  hideInitialPortfolio?: boolean;
}): ReactElement => {
  const { getValues, setValue } = useFormContext<AssetOptimizerInputFields>();
  const givenPortfolio = useWatch({ name: 'givenPortfolio' });

  const columns: ColDef<NotVerifiedAssetWithId>[] = useMemo(() => {
    const clusters = uniq(
      Object.values(assetIdToClusterToGroup).flatMap((clusterToGroup) => Object.keys(clusterToGroup))
    );

    return [
      {
        colId: 'assetName',
        headerName: 'Asset',
        valueGetter: (params: ValueGetterParams<NotVerifiedAssetWithId, string>): string | undefined => {
          if (!params.data) {
            return undefined;
          }

          return params.data.name ?? '';
        },
      },
      {
        headerName: 'Symbol',
        field: 'symbol',
      },
      {
        colId: 'assetType',
        headerName: 'Type',
        valueGetter: (params: ValueGetterParams<NotVerifiedAssetWithId, string>): string | undefined => {
          if (!params.data) {
            return undefined;
          }

          return getAssetCategory(params.data);
        },
      },
      ...clusters.map(
        (cluster): ColDef<NotVerifiedAssetWithId> => ({
          colId: `cluster-${cluster}`,
          headerName: cluster,
          valueGetter: (params: ValueGetterParams<NotVerifiedAssetWithId, string>): string | undefined => {
            if (!params.data) {
              return undefined;
            }

            const underlyingAssetId = params.data.id;
            if (!underlyingAssetId) {
              return undefined;
            }
            return assetIdToClusterToGroup[underlyingAssetId]?.[cluster];
          },
        })
      ),
      ...(!hideInitialPortfolio ? [createInInitialPortfolioColumn(givenPortfolio)] : []),
    ];
  }, [assetIdToClusterToGroup, givenPortfolio, hideInitialPortfolio]);

  return (
    <Stack spacing={defaultRowSpacing}>
      <Universe<NotVerifiedAssetWithId, AssetOptimizerInputFields>
        columnDefs={columns}
        rowData={assets}
        idGetter={(row) => row?.id ?? ''}
        syncSelection={({ getValues, setValue, clearErrors }) => {
          if (!syncWithOutlook) {
            return;
          }

          syncOutlookWithGivenUniverseFields(getValues, setValue, clearErrors);
        }}
        onChange={(rows) => {
          const availableAssetIds = new Set(rows.map((asset) => asset.id));
          const groupForSelectedAssets = getAssetsGroups(assetIdToClusterToGroup, assets);
          const constraintsWithGroupsOrExistingAssets = getValues('constraints').filter((con) => {
            switch (con.item?.type) {
              case 'asset':
                return con.item.asset?.id && availableAssetIds.has(con.item.asset.id);
              case 'group':
                return con.item.group?.groupName && groupForSelectedAssets.has(con.item.group.groupName);
              default:
                return true;
            }
          });

          setValue('constraints', constraintsWithGroupsOrExistingAssets);
        }}
      />
      <GButton onClick={goToNextStep} sx={{ marginLeft: 'auto' }}>
        Next
      </GButton>
    </Stack>
  );
};

export default AssetUniverseStep;
